import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { useLabPermissions } from "../../../hooks/useLabPermissions";
import { ASSISTANT_PERMISSION_OPTIONS } from "../../../utils/assistantPermissions";

const ROLE_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "administrative_assistant", label: "Auxiliar administrativo" },
  { value: "reception", label: "Recepción" },
  { value: "technician", label: "Técnico" },
  { value: "lab_manager", label: "Jefe de laboratorio" },
  { value: "accountant", label: "Contabilidad" },
  { value: "admin", label: "Administrador" },
  { value: "client", label: "Cliente" },
];

const ROLE_LABELS = Object.fromEntries(
  ROLE_OPTIONS.map((option) => [option.value, option.label])
);

function normalizePermissions(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value)
    .replace(/^\{|\}$/g, "")
    .split(",")
    .map((item) => item.replace(/^"|"$/g, "").trim())
    .filter(Boolean);
}

function UserPermissions({
  user,
  busyKey,
  onTogglePermission,
}) {
  const active = new Set(normalizePermissions(user.permissions));

  return (
    <div className="border-t border-white/10 bg-black/15 px-4 py-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-slate-100">
          Permisos adicionales del auxiliar
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Marcá solamente las tareas que esta persona puede realizar. Los cambios
          quedan protegidos también en la base de datos.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {ASSISTANT_PERMISSION_OPTIONS.map((permission) => {
          const key = `${user.id}:${permission.key}`;
          const checked = active.has(permission.key);
          const saving = busyKey === key;

          return (
            <label
              key={permission.key}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition ${
                checked
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : "border-white/10 bg-white/[0.025] hover:bg-white/[0.05]"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-500"
                checked={checked}
                disabled={saving}
                onChange={(event) =>
                  onTogglePermission(
                    user,
                    permission.key,
                    event.target.checked
                  )
                }
              />
              <span>
                <span className="block text-sm font-medium text-slate-100">
                  {permission.label}
                  {saving && (
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      Guardando…
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs leading-5 text-slate-400">
                  {permission.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function UsersAdminPage() {
  const { isAdmin } = useLabPermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc("admin_list_app_users");
      if (error) throw error;
      setRows(
        (data || []).map((row) => ({
          ...row,
          permissions: normalizePermissions(row.permissions),
        }))
      );
    } catch (error) {
      console.error("[UsersAdmin] load", error);
      setMessage({
        type: "error",
        text: error?.message || "No se pudieron cargar los usuarios.",
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (roleFilter && row.role !== roleFilter) return false;
      if (!needle) return true;
      return (
        String(row.full_name || "").toLowerCase().includes(needle) ||
        String(row.email || "").toLowerCase().includes(needle)
      );
    });
  }, [rows, query, roleFilter]);

  const handleRoleChange = async (user, nextRole) => {
    if (!nextRole || nextRole === user.role) return;
    const label = ROLE_LABELS[nextRole] || nextRole;
    if (
      !window.confirm(
        `¿Cambiar el rol de ${user.full_name || user.email} a "${label}"?`
      )
    ) {
      return;
    }

    const key = `${user.id}:role`;
    try {
      setBusyKey(key);
      setMessage(null);
      const { error } = await supabase.rpc("admin_set_profile_role", {
        p_user_id: user.id,
        p_role: nextRole,
      });
      if (error) throw error;

      setExpandedUserId(
        nextRole === "administrative_assistant" ? user.id : null
      );
      setMessage({
        type: "ok",
        text: `Rol actualizado a ${label}.`,
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo cambiar el rol.",
      });
    } finally {
      setBusyKey("");
    }
  };

  const handleTogglePermission = async (user, permission, enabled) => {
    const key = `${user.id}:${permission}`;

    setRows((current) =>
      current.map((row) => {
        if (row.id !== user.id) return row;
        const next = new Set(normalizePermissions(row.permissions));
        if (enabled) next.add(permission);
        else next.delete(permission);
        return { ...row, permissions: Array.from(next) };
      })
    );

    try {
      setBusyKey(key);
      setMessage(null);
      const { error } = await supabase.rpc("admin_set_user_permission", {
        p_user_id: user.id,
        p_permission: permission,
        p_enabled: enabled,
      });
      if (error) throw error;

      setMessage({
        type: "ok",
        text: enabled ? "Permiso concedido." : "Permiso retirado.",
      });
    } catch (error) {
      await load();
      setMessage({
        type: "error",
        text: error?.message || "No se pudo actualizar el permiso.",
      });
    } finally {
      setBusyKey("");
    }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl py-10">
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-5 text-rose-200">
          Solo un administrador puede gestionar usuarios y permisos.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-16">
      <header className="py-6">
        <h1 className="text-2xl font-semibold text-white">
          Usuarios y permisos
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Asigná roles a las cuentas registradas y habilitá tareas específicas
          para cada auxiliar administrativo.
        </p>
      </header>

      {message && (
        <div
          className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            message.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : "border-rose-500/30 bg-rose-500/10 text-rose-200"
          }`}
        >
          <span>{message.text}</span>
          <button
            type="button"
            className="opacity-70 hover:opacity-100"
            onClick={() => setMessage(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 md:grid-cols-[1fr_260px]">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar por nombre o email…"
          className="rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
        />
        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          className="rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2.5 text-sm text-white"
        >
          <option value="">Todos los roles</option>
          {ROLE_OPTIONS.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400">
          Cargando usuarios…
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-8 text-center text-sm text-slate-400">
          No hay usuarios para mostrar.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/10">
          {filteredRows.map((user) => {
            const isAssistant = user.role === "administrative_assistant";
            const expanded = expandedUserId === user.id;

            return (
              <div key={user.id} className="border-b border-white/10 last:border-b-0">
                <div className="grid grid-cols-1 items-center gap-3 px-4 py-4 md:grid-cols-[1fr_260px_auto]">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-white">
                      {user.full_name || "Sin nombre"}
                    </div>
                    <div className="truncate text-sm text-slate-400">
                      {user.email || "Sin email"}
                    </div>
                  </div>

                  <select
                    value={user.role || "pending"}
                    disabled={busyKey === `${user.id}:role`}
                    onChange={(event) =>
                      handleRoleChange(user, event.target.value)
                    }
                    className="rounded-lg border border-white/10 bg-slate-900/70 px-3 py-2 text-sm text-white disabled:opacity-60"
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>

                  {isAssistant ? (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedUserId(expanded ? null : user.id)
                      }
                      className="rounded-lg border border-emerald-500/40 px-3 py-2 text-sm text-emerald-200 hover:bg-emerald-500/10"
                    >
                      {expanded
                        ? "Ocultar permisos"
                        : `Permisos (${normalizePermissions(user.permissions).length})`}
                    </button>
                  ) : (
                    <span className="px-3 py-2 text-center text-xs text-slate-500">
                      Sin permisos extra
                    </span>
                  )}
                </div>

                {isAssistant && expanded && (
                  <UserPermissions
                    user={user}
                    busyKey={busyKey}
                    onTogglePermission={handleTogglePermission}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
